import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import type { ProjectConferenceTagWithMetadata } from '@trinity/chat/api/interfaces/projects';
import { TabViewTableSkeleton } from '@/components/TabView';
import { ProjectDetailsContext, ProjectStateLoadingContext } from '@/screens/Project.Main/Context';
import { TagStateContext } from './Context';
import { useTagStateReducer } from './hooks/useTagStateReducer';
import type { TagItem } from './interfaces';
import styles from './style/Project.Tags.module.css';
import { TagDetails } from './Tag.Details';
import { TagsTable } from './TagsTable';

export const ProjectTags = () => {
  const loading = useContext(ProjectStateLoadingContext);
  const projectDetails = useContext(ProjectDetailsContext);

  const [state, dispatch] = useTagStateReducer();

  // Get project ID from project context
  const projectId = projectDetails.query.data?.project.id;

  const apiClient = useChatApiClient();

  // React Query API call
  const query = useQuery({
    queryKey: ['get:projects/tags', projectId],
    queryFn: async () => {
      const response = await apiClient.projects.fetchProjectTags({ projectId });
      return response;
    },
    enabled: !!projectId && !loading,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const tags = useMemo(() => {
    if (!query.data) return [];
    return sortTags(query.data.tags.filter(t => !t.parent?.id).map(t => ({
      ...t,
      children: sortTags(query.data.tags.filter(c => c.parent?.id === t.id)),
    })));
  }, [query.data]);

  const params = useParams<{ tagId?: string }>();
  const tagId = params.tagId ? parseInt(params.tagId, 10) : null;

  const isViewingTagDetails = !!tagId;

  const selectedTag = useMemo(() => {
    if (!tagId) return null;

    const findTag = (tagsList: (ProjectConferenceTagWithMetadata | TagItem)[]): ProjectConferenceTagWithMetadata | null => {
      for (const tag of tagsList) {
        if (tag.id === tagId) return tag;
        if ('children' in tag && tag.children?.length > 0) {
          const found = findTag(tag.children);
          if (found) return found;
        }
      }
      return null;
    };

    return findTag(tags);
  }, [tagId, tags]);

  const initialize = useCallback(() => {
    dispatch({
      type: 'initialize',
      state: { tags },
    });
  }, [
    dispatch,
    tags,
  ]);

  useEffect(() => {

    if (query.isFetchedAfterMount) {
      initialize();
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [query.isFetchedAfterMount]);

  if (loading || query.isLoading) {
    return <TabViewTableSkeleton />;
  }

  if (query.error) {
    return (
      <div className={styles.root}>
        <div className={styles.wrap}>
          <div className={styles.errorContent}>
            <p>Error loading tags: {query.error.message}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!query.data?.tags?.length) {
    return (
      <div className={styles.root}>
        <div className={styles.wrap}>
          <div className={styles.emptyContent}>
            <p>No tags found for this project</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.wrap}>
        <TagStateContext.Provider value={{ dispatch, projectId, state }}>
          {isViewingTagDetails
            ? <TagDetails tag={selectedTag} />
            : <TagsTable tags={state.tags} />
          }
        </TagStateContext.Provider>
      </div>
    </div>
  );
};

function sortTags<T extends ProjectConferenceTagWithMetadata>(tags: T[]) {
  return tags.sort((a, b) => (a.ordinal ?? Number.MAX_VALUE) - (b.ordinal ?? Number.MAX_VALUE));
}